import React, { useEffect, useMemo, useState } from "react";
import { getTransactions } from "../../services/api";
import { getCurrentUser } from "../../services/auth";
import toast from "react-hot-toast";
import TransactionModal from "./TransactionModal";
import ContributionTypeModal from "./ContributionTypeModal";
import TransactionActionModal from "./TransactionActionModal";
import TransactionApprovals from "./TransactionApprovals";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./transactions.css";

export default function TransactionsPage() {
  const user = getCurrentUser();

  const isParokiaAdmin = user?.role === "PAROKIA_ADMIN";
  const isCashier = user?.role === "PAROKIA_CASHIER";
  const isSystemAdmin = user?.role === "SYSTEM_ADMIN";

  const canPostTransaction = isParokiaAdmin || isCashier;
  const canManageTransaction = isCashier || isParokiaAdmin;
  const canApproveTransactions = isParokiaAdmin;

  const [activeTab, setActiveTab] = useState("list");

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showCreate, setShowCreate] = useState(false);
  const [showTypes, setShowTypes] = useState(false);

  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    status: "",
    channel: "",
    search: "",
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const res = await getTransactions();
      setTransactions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load transactions", err);
      toast.error("Failed to load transactions");
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      dateFrom: "",
      dateTo: "",
      status: "",
      channel: "",
      search: "",
    });
    setPage(1);
  };

  const formatMoney = (value) => {
    const num = Number(value || 0);
    return `TSH.${num.toLocaleString()}`;
  };

  const formatDateOnly = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-GB");
  };

  const filteredTransactions = useMemo(() => {
    return transactions.filter((t) => {
      const q = filters.search.trim().toLowerCase();
      const transactionDate = t.transaction_date ? new Date(t.transaction_date) : null;

      const fromOk = !filters.dateFrom
        ? true
        : transactionDate
        ? transactionDate >= new Date(`${filters.dateFrom}T00:00:00`)
        : false;

      const toOk = !filters.dateTo
        ? true
        : transactionDate
        ? transactionDate <= new Date(`${filters.dateTo}T23:59:59`)
        : false;

      const statusOk = !filters.status ? true : t.status === filters.status;
      const channelOk = !filters.channel ? true : t.channel === filters.channel;

      const searchOk = !q
        ? true
        : [
            t.transaction_id,
            t.transaction_code,
            t.reference_no,
            t.full_name,
            t.phone,
            t.member_code,
            t.contribution_type,
            t.cashier_name,
            t.posted_by_username,
            t.parokia_name,
            t.kanda_name,
            t.jumuia_name,
            t.jimbo_name,
            t.jumuia_leader,
          ]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q));

      return fromOk && toOk && statusOk && channelOk && searchOk;
    });
  }, [transactions, filters]);

  const totalPages = Math.ceil(filteredTransactions.length / pageSize);

  const paginatedTransactions = filteredTransactions.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const startRow =
    filteredTransactions.length === 0 ? 0 : (page - 1) * pageSize + 1;

  const endRow = Math.min(page * pageSize, filteredTransactions.length);

  const downloadExcel = () => {
    if (filteredTransactions.length === 0) {
      toast.error("No transactions to export");
      return;
    }

    const data = filteredTransactions.map((t) => ({
      TransactionID: t.transaction_id,
      TransactionCode: t.transaction_code,
      ReferenceNo: t.reference_no,
      MemberName: t.full_name,
      Phone: t.phone,
      Amount: t.amount,
      ContributionType: t.contribution_type,
      Status: t.status,
      Cashier: t.cashier_name,
      TransactionDate: t.transaction_date,
      AddedDate: t.created_at,
      Channel: t.channel,
      Jimbo: t.jimbo_name,
      Parokia: t.parokia_name,
      Kanda: t.kanda_name,
      Jumuia: t.jumuia_name,
      PostedBy: t.posted_by_username,
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");

    const wbout = XLSX.write(workbook, {
      type: "array",
      bookType: "xlsx",
    });

    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      "transactions.xlsx"
    );
  };

  const openManageTransaction = (transaction) => {
    if (!canManageTransaction) {
      toast.error("You are not allowed to manage transactions");
      return;
    }

    if (Number(transaction.has_pending_request) === 1) {
      toast.error("Approval request already pending");
      return;
    }

    if (transaction.status === "REVERSED") {
      toast.error("Reversed transaction cannot be managed");
      return;
    }

    setSelectedTransaction(transaction);
  };

  const handleTransactionActionSubmitted = async () => {
    setSelectedTransaction(null);
    await loadTransactions();
  };

  const handleTransactionCreated = async () => {
    setShowCreate(false);
    await loadTransactions();
  };

  return (
    <div className="page-container">
      <div className="transactions-page-header">
        <div>
          <h1>Transactions</h1>
          <p className="subtitle">
            Manage and record church collections
          </p>
        </div>

        <div className="transactions-header-actions">
          {activeTab === "list" && (
            <>
              <button className="btn dark" onClick={downloadExcel}>
                Download Excel File
              </button>

              <button
                className="btn dark faded"
                onClick={() => toast("Reconciliation action not yet connected")}
              >
                Reconciliation Cash
              </button>

              <button
                className="btn dark faded"
                onClick={() => toast("Cash collection import not yet connected")}
              >
                Cash Collection Import
              </button>

              {isParokiaAdmin && (
                <button className="btn dark" onClick={() => setShowTypes(true)}>
                  Contribution Types
                </button>
              )}

              {canPostTransaction && (
                <button className="btn primary" onClick={() => setShowCreate(true)}>
                  Add New
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="transactions-tabs enterprise-compact-tabs">
  <button
    className={activeTab === "list" ? "active" : ""}
    onClick={() => setActiveTab("list")}
  >
    Transaction List
  </button>

  {canApproveTransactions && (
    <button
      className={activeTab === "approvals" ? "active" : ""}
      onClick={() => setActiveTab("approvals")}
    >
      Transactions Needing Approval
      <span className="compact-tab-badge">
         {transactions.filter((t) => Number(t.has_pending_request) === 1).length}
      </span>
    </button>
  )}
</div>

      {activeTab === "list" && (
        <>
          <div className="card transaction-filter-card">
            <div className="transaction-filter-grid">
              <input
                type="date"
                className="input"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
              />

              <input
                type="date"
                className="input"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange("dateTo", e.target.value)}
              />

              <select
                className="input"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="">Select Status</option>
                <option value="POSTED">POSTED</option>
                <option value="PENDING_APPROVAL">PENDING APPROVAL</option>
                <option value="REVERSED">REVERSED</option>
              </select>

              <select
                className="input"
                value={filters.channel}
                onChange={(e) => handleFilterChange("channel", e.target.value)}
              >
                <option value="">Select Channel</option>
                <option value="CASH">CASH</option>
                <option value="BANK">BANK</option>
              </select>

              <input
                className="input"
                placeholder="Search by member, code, phone, reference..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
              />

              <div className="transaction-filter-actions">
                <button className="btn primary">Search</button>
                <button className="btn light" onClick={clearFilters}>
                  Clear
                </button>
              </div>
            </div>
          </div>

          <div className="card">
            {loading ? (
              <div className="spinner-wrap">
                <div className="spinner" />
              </div>
            ) : (
              <>
                <div className="transaction-table-wrap">
                  <table className="table transaction-table">
                    <thead>
                      <tr>
                        <th style={{ width: "15%" }}>Trans ID</th>
                        <th style={{ width: "17%" }}>Transaction Details</th>
                        <th style={{ width: "17%" }}>Member Details</th>
                        <th style={{ width: "15%" }}>Facility Details</th>
                        <th style={{ width: "25%" }}>Others Details</th>
                        <th style={{ width: "11%" }}>Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {paginatedTransactions.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="empty">
                            No transactions found
                          </td>
                        </tr>
                      ) : (
                        paginatedTransactions.map((t) => (
                          <tr key={t.transaction_id}>
                            <td className="transaction-id-cell">
                              {t.transaction_code ||
                                t.reference_no ||
                                `TRX-${t.transaction_id}`}
                            </td>

                            <td>
                              <div className="detail-line">
                                <strong>Trans Amount :</strong>{" "}
                                {formatMoney(t.amount)}
                              </div>
                              <div className="detail-line">
                                <strong>Trans Type :</strong>{" "}
                                {t.contribution_type || "-"}
                              </div>
                              <div className="detail-line">
                                <strong>Trans Status :</strong>{" "}
                                <span
                                  className={`approval-badge ${String(
                                    t.status || ""
                                  ).toLowerCase()}`}
                                >
                                  {t.status || "-"}
                                </span>
                              </div>
                              <div className="detail-line">
                                <strong>Cashier Name :</strong>{" "}
                                {t.cashier_name || "-"}
                              </div>
                              <div className="detail-line">
                                <strong>Trans Date :</strong>{" "}
                                {formatDateOnly(t.transaction_date)}
                              </div>
                              <div className="detail-line">
                                <strong>Added Date :</strong>{" "}
                                {formatDateOnly(t.created_at)}
                              </div>
                            </td>

                            <td>
                              <div className="detail-line">
                                <strong>Name :</strong> {t.full_name || "-"}
                              </div>
                              <div className="detail-line">
                                <strong>Account ID :</strong>{" "}
                                {t.member_code || "-"}
                              </div>
                              <div className="detail-line">
                                <strong>API ID :</strong> {t.member_id || "-"}
                              </div>
                              <div className="detail-line">
                                <strong>Mobile No :</strong> {t.phone || "-"}
                              </div>
                            </td>

                            <td>
                              <div className="detail-line">
                                <strong>Channel :</strong> {t.channel || "-"}
                              </div>
                              <div className="detail-line">
                                <strong>Reference :</strong>{" "}
                                {t.reference_no || "-"}
                              </div>
                              <div className="detail-line">
                                <strong>Member ID :</strong> {t.member_id || "-"}
                              </div>
                              <div className="detail-line">
                                <strong>Facility ID :</strong>{" "}
                                {t.facility_id || "-"}
                              </div>
                            </td>

                            <td>
                              <div className="detail-line">
                                <strong>Jimbo :</strong> {t.jimbo_name || "-"}
                              </div>
                              <div className="detail-line">
                                <strong>Parokia :</strong>{" "}
                                {t.parokia_name || "-"}
                              </div>
                              <div className="detail-line">
                                <strong>Kanda :</strong> {t.kanda_name || "-"}
                              </div>
                              <div className="detail-line">
                                <strong>Jumuiya :</strong>{" "}
                                {t.jumuia_name || "-"}
                              </div>
                              <div className="detail-line">
                                <strong>Posted By :</strong>{" "}
                                {t.posted_by_username || "-"}
                              </div>
                            </td>

                            <td className="transaction-action-cell">
                              {canManageTransaction ? (
                                <button
                                  className={
                                    t.status === "PENDING_APPROVAL" ||
                                    t.status === "REVERSED"
                                      ? "btn small light"
                                      : "btn small primary"
                                  }
                                  disabled={
                                    Number(t.has_pending_request) === 1 ||
                                    t.status === "REVERSED"
                                  }
                                  onClick={() => openManageTransaction(t)}
                                >
                                  {Number(t.has_pending_request) === 1
                                    ? "Pending"
                                    : t.status === "REVERSED"
                                    ? "Reversed"
                                    : "Manage"}
                                </button>
                              ) : (
                                <span className="subtitle">View Only</span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="pagination-controls">
                  <button
                    className="btn light"
                    disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>

                  <span>
                    Page {page} of {totalPages || 1} | Showing {startRow}-{endRow}{" "}
                    of {filteredTransactions.length} rows
                  </span>

                  <button
                    className="btn light"
                    disabled={page === totalPages || totalPages === 0}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </button>

                  <select
                    className="input"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    {[10, 20, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size} per page
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {activeTab === "approvals" && canApproveTransactions && (
        <TransactionApprovals />
      )}

      {showCreate && canPostTransaction && (
        <TransactionModal
          onClose={() => setShowCreate(false)}
          onSuccess={handleTransactionCreated}
        />
      )}

      {showTypes && isParokiaAdmin && (
        <ContributionTypeModal onClose={() => setShowTypes(false)} />
      )}

      {selectedTransaction && (
        <TransactionActionModal
          transaction={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
          onSuccess={handleTransactionActionSubmitted}
        />
      )}
    </div>
  );
}